/* eslint-disable no-param-reassign */
import {
  cloneDeep, isEmpty, last as dashLast, omit, pick, get,
} from 'lodash';
import consultationService from '@/services/tcm-consultation.service';
import constant from '@/services/constant';
import { v4 } from 'uuid';

import {
  SET_TCM_CONSULTATION_LIST,
  SET_TCM_SELECTED_CONSULTATION_HISTORY,
  SET_TCM_SELECTED_CONSULTATION_BY_QUEUE,
  SET_TCM_ACTIVE_HISTORY_PRESCRIPTION,
  SET_TCM_ACTIVE_CONSULTATION,
  SET_TCM_ACTIVE_PRESCRIPTION,
  SET_TCM_ACTIVE_VISITRECORD,
  UPDATE_VISITRECORD_FORM,
  SET_ACTIVE_TAB,
  SET_REFER_ROOT_SHORTCUTS,
  UPDATE_INVOICE,
  UPDATE_INVOICES,
  ADD_LINE_ITEM,
  SET_INVOICES,
  UPDATE_INVOICE_GST,
  UPDATE_INVOICE_GST_X_RATE,
  SAVE_INVOICE,
  REMOVE_LINE_ITEM,
  REMOVE_LINE_ITEM_BY_ITEM_ID,
  REMOVE_LINE_ITEM_BY_ITEM_ID_LIST,
  REMOVE_INVOICE,
  REMOVE_ALL_LINE_ITEMS,
  BULK_UPDATE_INVOICE,
  SET_PRINT,
  SET_PRINTABLE_DATA,
  SET_PRINT_TREATMENT_RECORD_CONSULTATION,
  SET_PRINT_PRESCRIPTION_CONSULTATION,
  RESET_UPDATED_DATASET,
  SET_IS_FETCH_LAST_DATA
} from './type.mutation';

const state = {
  tcmPrescriptionPrintPayload: null,
  consultationList: [],
  selectedConsultationHistory: {},
  activeHistoryPrescription: {},
  selectedConsultationIdByQueue: null,
  selectedConsultation: {},
  activePrescription: {},
  activeVisitRecord: {},

  visitRecordForm: {},
  activeTab: 0,
  referRootShortcuts: {}, // { mainComplaint: [item1, item2, item3, ....]}

  invoicesMappedById: {},
  invoicesToPrint: [],
  dataToPrint: null,
  treatmentRecordConsultationToPrint: {},
  prescriptionInConsultationToPrint: {},
  consultationLastUpdated: null,
  orderLastUpdated: null,
  visitRecordLastUpdated: null,
  invoiceLastUpdated: null,
  copyConsultationLastUpdated: null,
  isFetchLastData: false
};

const mutations = {
  setTcmPrintPrescription(_state, payload) {
    _state.tcmPrescriptionPrintPayload = payload;
  },
  [SET_TCM_SELECTED_CONSULTATION_BY_QUEUE](_state, consultationId) {
    _state.selectedConsultationIdByQueue = consultationId;
  },
  [SET_TCM_CONSULTATION_LIST](_state, payload) {
    _state.consultationList = payload;
  },
  [SET_TCM_SELECTED_CONSULTATION_HISTORY](_state, payload) {
    _state.selectedConsultationHistory = payload;
  },
  [SET_TCM_ACTIVE_HISTORY_PRESCRIPTION](_state, payload) {
    _state.activeHistoryPrescription = payload;
  },
  [SET_TCM_ACTIVE_CONSULTATION](_state, payload) {
    _state.selectedConsultation = payload;
    const { visitRecord } = payload;
    const {
      __v, _id, clinic, patient, visitDate, updatedAt, createdAt, ...rest
    } = cloneDeep(visitRecord || {});
    _state.visitRecordForm = rest || {};
  },
  [SET_TCM_ACTIVE_PRESCRIPTION](_state, payload) {
    _state.activePrescription = payload;
  },
  [SET_TCM_ACTIVE_VISITRECORD](_state, payload) {
    _state.activeVisitRecord = payload;
  },
  [UPDATE_VISITRECORD_FORM](_state, payload) {
    _state.visitRecordForm = payload;
  },
  [SET_ACTIVE_TAB](_state, payload) {
    _state.activeTab = payload;
  },
  [SET_REFER_ROOT_SHORTCUTS](_state, payload) {
    _state.referRootShortcuts = payload;
  },

  // Order/Invoice/Prescription
  [SET_INVOICES](_state, payload) {
    _state.invoicesMappedById = payload;
  },
  [ADD_LINE_ITEM](_state, { invoiceId, item }) {
    const existingInvoice = _state.invoicesMappedById[invoiceId] ? _state.invoicesMappedById[invoiceId] : { items: false };
    const clonedItems = cloneDeep(existingInvoice.items || []);
    if (!item.uuid) {
      item.uuid = v4();
    }
    clonedItems.push(item);
    _state.invoicesMappedById = {
      ..._state.invoicesMappedById,
      [invoiceId]: { ..._state.invoicesMappedById[invoiceId], items: clonedItems },
    };

    _state.activePrescription = _state.invoicesMappedById[_state.activePrescription._id] || _state.activePrescription;
  },
  [REMOVE_LINE_ITEM](_state, { invoiceId, uuid }) {
    let clonedItems = cloneDeep(_state.invoicesMappedById[invoiceId].items) || [];
    clonedItems = clonedItems.filter(item => item.uuid !== uuid);
    _state.invoicesMappedById = { ..._state.invoicesMappedById, [invoiceId]: { ..._state.invoicesMappedById[invoiceId], items: clonedItems } };
    _state.activePrescription = _state.invoicesMappedById[_state.activePrescription._id] || _state.activePrescription;
  },
  [REMOVE_ALL_LINE_ITEMS](_state, { invoiceId }) {
    _state.invoicesMappedById = { ..._state.invoicesMappedById, [invoiceId]: { ..._state.invoicesMappedById[invoiceId], items: [] } };
    _state.activePrescription = _state.invoicesMappedById[_state.activePrescription._id] || _state.activePrescription;
  },
  [REMOVE_LINE_ITEM_BY_ITEM_ID](_state, { invoiceId, itemId }) {
    let clonedItems = cloneDeep(_state.invoicesMappedById[invoiceId].items) || [];
    clonedItems = clonedItems.filter(item => item._id !== itemId);
    _state.invoicesMappedById = { ..._state.invoicesMappedById, [invoiceId]: { ..._state.invoicesMappedById[invoiceId], items: clonedItems } };
    _state.activePrescription = _state.invoicesMappedById[_state.activePrescription._id] || _state.activePrescription;
  },
  [REMOVE_LINE_ITEM_BY_ITEM_ID_LIST](_state, { invoiceId, itemIdList }) {
    const itemIdSet = new Set(itemIdList);
    let clonedItems = cloneDeep(_state.invoicesMappedById[invoiceId].items) || [];
    clonedItems = clonedItems.filter(item => !itemIdSet.has(item._id));
    _state.invoicesMappedById = { ..._state.invoicesMappedById, [invoiceId]: { ..._state.invoicesMappedById[invoiceId], items: clonedItems } };
    _state.activePrescription = _state.invoicesMappedById[_state.activePrescription._id] || _state.activePrescription;
  },
  [REMOVE_INVOICE](_state, { invoiceId }) {
    _state.invoicesMappedById = omit(_state.invoicesMappedById, invoiceId);
    _state.activePrescription = _state.invoicesMappedById[_state.activePrescription._id] || _state.activePrescription;
  },
  [UPDATE_INVOICE](_state, invoice) {
    _state.invoicesMappedById = { ..._state.invoicesMappedById, [invoice._id]: invoice };
    _state.activePrescription = _state.invoicesMappedById[_state.activePrescription._id] || _state.activePrescription;
  },
  [UPDATE_INVOICES](_state, invoices) {
    const invoicesById = invoices.reduce((invById, i) => {
      if (i._id) {
        invById[i._id] = i;
      }
      return invById;
    }, {});
    _state.invoicesMappedById = { ..._state.invoicesMappedById, ...invoicesById };
    _state.activePrescription = _state.invoicesMappedById[_state.activePrescription._id] || _state.activePrescription;
  },
  [UPDATE_INVOICE_GST](_state, { invoice, includeGST }) {
    _state.invoicesMappedById = {
      ..._state.invoicesMappedById,
      [invoice._id]: { ...invoice, includeGST },
    };
    _state.activePrescription = _state.invoicesMappedById[_state.activePrescription._id] || _state.activePrescription;
  },
  [UPDATE_INVOICE_GST_X_RATE](_state, { invoice, includeGST, exchangeRate }) {
    _state.invoicesMappedById = {
      ..._state.invoicesMappedById,
      [invoice._id]: { ...invoice, includeGST, exchangeRate },
    };
    _state.activePrescription = _state.invoicesMappedById[_state.activePrescription._id] || _state.activePrescription;
  },
  [BULK_UPDATE_INVOICE](_state, { invoiceIdList, payload }) {
    invoiceIdList.forEach((invoiceId) => {
      const inv = _state.invoicesMappedById[invoiceId];
      if (isEmpty(inv)) return;
      _state.invoicesMappedById = {
        ..._state.invoicesMappedById,
        [inv._id]: { ...inv, ...payload },
      };
    });
    _state.activePrescription = _state.invoicesMappedById[_state.activePrescription._id] || _state.activePrescription;
  },
  [SET_PRINT](_state, payload) {
    _state.invoicesToPrint = payload.filter(i => i.items && i.items.length > 0);
  },
  [SET_PRINTABLE_DATA](_state, payload) {
    _state.dataToPrint = payload;
  },
  [SET_PRINT_TREATMENT_RECORD_CONSULTATION](_state, payload) {
    _state.treatmentRecordConsultationToPrint = payload;
  },
  [SET_PRINT_PRESCRIPTION_CONSULTATION](_state, { consultation, printWithDailyDosage }) {
    const payload = { ...consultation, printWithDailyDosage };
    _state.prescriptionInConsultationToPrint = payload;
  },
  [RESET_UPDATED_DATASET](_state) {
    _state.consultationLastUpdated = null;
    _state.orderLastUpdated = null;
    _state.visitRecordLastUpdated = null;
    _state.invoiceLastUpdated = null;
    _state.copyConsultationLastUpdated = null;
  },
  [SET_IS_FETCH_LAST_DATA](_state, payload) {
    _state.isFetchLastData = payload
  },
  /* Socket events */
  SOCKET_TCM_CONSULTATION_UPDATED(_state, consultation) {
    const data = cloneDeep(_state.consultationLastUpdated)
    _state.consultationLastUpdated = {
      ...data,
      [consultation._id]: consultation.updatedAt
    };
  },
  SOCKET_TCM_ORDER_UPDATED(_state, order) {
    const data = cloneDeep(_state.orderLastUpdated)
    _state.orderLastUpdated = {
      ...data,
      [order._id]: order.updatedAt
    };
  },
  SOCKET_TCM_VISIT_RECORD_UPDATED(_state, consultation) {
    const data = cloneDeep(_state.visitRecordLastUpdated)
    _state.visitRecordLastUpdated = {
      ...data,
      [consultation._id]: consultation.visitRecord
    };
  },
  SOCKET_TCM_INVOICE_UPDATED(_state, invoice) {
    const data = cloneDeep(_state.invoiceLastUpdated)
    _state.invoiceLastUpdated = {
      ...data,
      [invoice._id]: invoice.updatedAt
    };
  },
  SOCKET_TCM_COPY_UPDATED(_state, copyData) {
    const {data, type} = copyData;
    _state.copyConsultationLastUpdated = {
      [data._id]: {
        updatedAt: data.updatedAt,
        type: type
      }
    };
  }
  /* e: Socket events */
  /*
  [PATCH_STATS](_state, payload) {
    const clonedStats = { ..._state.stats };
    clonedStats[payload.id] = { ...clonedStats[payload.id], ...{ ostBalance: payload.ostBalance } };
    _state.stats = clonedStats;
  },
  */
};

const actions = {
  resetAll({ commit }) {
    commit(SET_INVOICES, []);
    commit(SET_TCM_CONSULTATION_LIST, []);
    commit(SET_TCM_SELECTED_CONSULTATION_HISTORY, {});
    commit(SET_TCM_ACTIVE_HISTORY_PRESCRIPTION, {});
    commit(SET_TCM_ACTIVE_CONSULTATION, {});
    commit(SET_TCM_ACTIVE_PRESCRIPTION, {});
    commit(SET_TCM_ACTIVE_VISITRECORD, {});
    // TODO: we need to keep this for tcm consultation tab. I have no better idea now
    // commit(SET_TCM_SELECTED_CONSULTATION_BY_QUEUE, null);
  },
  fetchConsultationList({ commit, getters, state: _state }, { params }) {
    return consultationService.list(params)
      .then((resp) => {
        commit(SET_TCM_CONSULTATION_LIST, resp.data);
        // update invoices
        const invoices = [];
        getters.openConsultationList.forEach((con) => {
          const { order } = con;
          order.forEach((inv) => {
            invoices.push(inv);
          });
        });
        commit(UPDATE_INVOICES, invoices);
        if (_state.selectedConsultation && _state.selectedConsultation._id) {
          const payload = resp.data.find(i => i._id === _state.selectedConsultation._id);
          if (payload) {
            commit(SET_TCM_ACTIVE_CONSULTATION, payload);
            const { order } = payload;
            let prescription = {};
            if (order) {
              [prescription] = order;
            }
            commit(SET_TCM_ACTIVE_PRESCRIPTION, prescription);
          }
        }
        return resp.data;
      });
  },
  fetchSelectConsultationData({ commit, state: _state }, prescriptionAction = false) {
    if (isEmpty(_state.selectedConsultation)) return;
    return consultationService.list({
      id: _state.selectedConsultation._id,
    }).then((resp) => {
      const payload = resp.data.find(i => i._id === _state.selectedConsultation._id);
      if (payload) {
        if (prescriptionAction && payload.status !== "Finalized") {
          return payload;
        }
        const { consultationList } = _state;
        const newList = consultationList.map((c) => {
          if (c._id !== payload._id) return c;
          return payload;
        });

        const invoicesMappedById = cloneDeep(_state.invoicesMappedById);

        const { order } = payload;
        let prescription = {};
        if (order) {
          [prescription] = order;
        }

        order.forEach((item) => {
          invoicesMappedById[item._id] = item
        })

        commit(SET_IS_FETCH_LAST_DATA, true);
        commit(SET_TCM_CONSULTATION_LIST, newList);
        commit(SET_TCM_ACTIVE_CONSULTATION, payload);
        commit(SET_TCM_ACTIVE_PRESCRIPTION, prescription);
        commit(SET_INVOICES, invoicesMappedById);
        // setTimeout make watch can get true
        setTimeout(()=> {
          commit(SET_IS_FETCH_LAST_DATA, false);
        }, 500)

      }
      return payload;
    });
  },
  selectHistoryById({ commit, state: _state }, consultationId) {
    if (!consultationId) {
      commit(SET_TCM_SELECTED_CONSULTATION_HISTORY, {});
      commit(SET_TCM_ACTIVE_HISTORY_PRESCRIPTION, {});
      return;
    }
    const { consultationList } = _state;
    const consultation = consultationList.find(c => c._id === consultationId);
    if (consultation) {
      commit(SET_TCM_SELECTED_CONSULTATION_HISTORY, consultation);
      const { order } = consultation;
      if (order) {
        const [firstPrescrition] = order;
        commit(SET_TCM_ACTIVE_HISTORY_PRESCRIPTION, firstPrescrition || {});
      } else {
        commit(SET_TCM_ACTIVE_HISTORY_PRESCRIPTION, {});
      }
    }
  },
  selectHistoryPrescriptionById({ commit, state: _state }, prescriptionId) {
    const { selectedConsultationHistory } = _state;
    if (selectedConsultationHistory) {
      const { order } = selectedConsultationHistory;
      const prescription = order.find(o => o._id === prescriptionId);
      if (prescription) {
        commit(SET_TCM_ACTIVE_HISTORY_PRESCRIPTION, prescription);
      }
    }
  },
  selectPrescriptionById({ commit, state: _state }, prescriptionId) {
    const { selectedConsultation } = _state;
    if (selectedConsultation) {
      const prescription = _state.invoicesMappedById[prescriptionId];
      if (prescription) {
        commit(SET_TCM_ACTIVE_PRESCRIPTION, prescription);
      }
    }
  },
  copyConsultation({ commit, state: _state }, { payload }) {
    const { type } = payload;
    if (!['all', 'visitRecord', 'singlePrescription', 'allPrescription'].includes(type)) {
      return false;
    }
    const { selectedConsultation, consultationList } = _state;
    payload.to = selectedConsultation ? selectedConsultation._id : undefined;
    return consultationService.copy(payload)
      .then((resp) => {
        const consultation = resp.data;
        const { order } = consultation;
        if (order) {
          const [firstPrescrition] = order;
          commit(SET_TCM_ACTIVE_PRESCRIPTION, firstPrescrition || {});
          if (order.length > 0) {
            commit(UPDATE_INVOICES, order);
          }
        } else {
          commit(SET_TCM_ACTIVE_PRESCRIPTION, {});
        }
        // update list
        const newList = consultationList.map((c) => {
          if (c._id !== consultation._id) return c;
          return consultation;
        });
        // TODO: refactor, update in Mutations
        commit(SET_TCM_CONSULTATION_LIST, newList);
        commit(SET_TCM_ACTIVE_CONSULTATION, consultation);
        return consultation;
      });
  },
  selectConsultation({ commit }, payload) {
    if (payload) {
      commit(SET_TCM_ACTIVE_CONSULTATION, payload);
    }
    if (payload) {
      const { order } = payload;
      if (!order) {
        commit(SET_TCM_ACTIVE_PRESCRIPTION, {});
        return;
      }
      const [prescription] = order;
      if (prescription) {
        commit(SET_TCM_ACTIVE_PRESCRIPTION, prescription);
      }
    }
  },
  selectConsultationById({ commit, state: _state }, consultationId, reset = false) {
    const { consultationList } = _state;
    const consultation = consultationList.find(c => c._id === consultationId);
    if (consultation) {
      commit(SET_TCM_ACTIVE_CONSULTATION, consultation);
    } else if (reset) {
      commit(SET_TCM_ACTIVE_CONSULTATION, {});
    }
  },
  updateConsultationInList({ commit, state: _state }, consultation) {
    const { consultationList } = _state;
    const newList = consultationList.map(c => {
      if (c._id !== consultation._id) return c;
      return consultation;
    });
    commit(SET_TCM_CONSULTATION_LIST, newList);
  },
  resetSelectedConsultationByQueue({ commit }) {
    commit(SET_TCM_SELECTED_CONSULTATION_BY_QUEUE, null);
  },
  setPendingTcmConsultationId({ commit }, consultationId) {
    commit();
  },
  updateVisitRecordForm({ commit, state: _state }, payload) {
    const { visitRecordForm } = _state;
    const form = cloneDeep(visitRecordForm);
    commit(UPDATE_VISITRECORD_FORM, { ...form, ...payload });
  },
  updateActivePrescriptionForm({ commit, state: _state }, payload) {
    const { activePrescription } = _state;
    const form = cloneDeep(activePrescription);
    commit(SET_TCM_ACTIVE_PRESCRIPTION, { ...form, ...payload });
  },
  setActiveTab({ commit }, tabName) {
    const TABS = [
      'consultationHistory',
      'diagnosisTreatment',
      'prescription',
      'settings',
    ];
    const index = TABS.includes(tabName) ? TABS.indexOf(tabName) : 0;
    commit(SET_ACTIVE_TAB, index);
  },
  resetVisitRecordForm({ commit }) {
    commit(UPDATE_VISITRECORD_FORM, {});
  },
  updateVisitRecord({ commit, state: _state }, { id, payload, isAutoSave=false }) {
    // NOTE: payload should have a field 'consultationId'
    return consultationService.patchVisitRecord(id, payload)
      .then((resp) => {
        // note this returns a consultation Record
        const consultation = resp.data;
        if (!consultation) return {};
        // replace the one in list
        const { consultationList } = _state;
        const newList = consultationList.map((c) => {
          if (c._id !== consultation._id) return c;
          return consultation;
        });
        commit(SET_TCM_CONSULTATION_LIST, newList);
        if (consultation) {
          if (consultation.order && consultation.order.length > 0) {
            commit(UPDATE_INVOICES, consultation.order);
          }
          commit(SET_TCM_ACTIVE_CONSULTATION, consultation);
        }
        return consultation;
      });
  },
  updateTreatmentRecord({ commit, state: _state }, {
    visitRecordId, consultationId, treatmentRecord,
  }) {
    return consultationService.patchVisitRecord(visitRecordId, { consultationId, treatmentRecord })
      .then((resp) => {
        const consultation = resp.data;
        if (!consultation) return {};
        const { consultationList, selectedConsultation, selectedConsultationHistory } = _state;
        const newList = consultationList.map((c) => {
          if (c._id !== consultation._id) return c;
          return consultation;
        });
        commit(SET_TCM_CONSULTATION_LIST, newList);
        if (selectedConsultation && selectedConsultation._id === consultation._id) {
          commit(SET_TCM_ACTIVE_CONSULTATION, consultation);
        }
        if (selectedConsultationHistory._id === consultation._id) {
          commit(SET_TCM_SELECTED_CONSULTATION_HISTORY, consultation);
        }
        return consultation;
      });
  },
  fetchReferenceRootAndShortcuts({ commit }) {
    return consultationService.getReferenceIndex()
      .then((resp) => {
        const items = resp.data;
        const payload = {};
        items.forEach((item) => {
          const { type } = item;
          if (!payload[type]) {
            payload[type] = [item];
          } else {
            payload[type].push(item);
          }
        });
        // TODO sort here or in component
        commit(SET_REFER_ROOT_SHORTCUTS, payload);
        return payload;
      });
  },
  removePS({ commit, state: _state }, { payload }) {
    return consultationService.removePS(payload)
      .then((resp) => {
        const { orderId } = payload;
        commit(REMOVE_INVOICE, { invoiceId: orderId });
        // // note this returns a consultation Record
        const consultation = resp.data;

        const { consultationList } = _state;
        const newList = consultationList.map((c) => {
          if (c._id !== consultation._id) return c;
          const clonedConsultation = cloneDeep(c);
          clonedConsultation.order = c.order.filter(o => o._id !== orderId);
          return clonedConsultation;
        });
        commit(SET_TCM_CONSULTATION_LIST, newList);
        if (consultation) {
          const { order } = consultation;
          const prescription = dashLast(order);
          const invoice = _state.invoicesMappedById[prescription._id];
          if (prescription) {
            commit(SET_TCM_ACTIVE_PRESCRIPTION, invoice);
          }
        }
        return consultation;
      });
  },
  addNewPS({ commit, state: _state }, { payload }) {
    return consultationService.addNewPS(payload)
      .then((resp) => {
        // note this returns a consultation Record
        const consultation = resp.data;
        if (!consultation) return {};
        // replace the one in list
        const { consultationList } = _state;
        const newList = consultationList.map((c) => {
          if (c._id !== consultation._id) return c;
          return consultation;
        });
        commit(SET_TCM_CONSULTATION_LIST, newList);
        commit(SET_TCM_ACTIVE_CONSULTATION, consultation);

        const { order } = consultation;
        const prescription = dashLast(order);
        if (prescription) {
          commit(SET_TCM_ACTIVE_PRESCRIPTION, prescription);
        }
        if (order.length > 0) {
          commit(UPDATE_INVOICES, order);
        }
        return consultation;
      });
  },
  async [SAVE_INVOICE]({ commit, state: _state }, { id, voidInvoice, closeInvoice }) {
    let payload = Object.assign({}, _state.invoicesMappedById[id]);
    const clonedMainFormId = { ...payload.mainFormId };
    // misc
    payload.amountApply = 0;
    payload.corpAmountApply = 0;

    if (payload.queue) {
      if (payload.queue.provider && !payload.provider) payload.provider = payload.queue.provider;
      if (payload.queue.therapist && !payload.therapist) {
        payload.therapist = payload.queue.therapist;
      }
    }

    if (payload.provider && typeof payload.provider === 'object') {
      payload.provider = payload.provider._id;
    }
    if (payload.therapist && typeof payload.therapist === 'object') {
      payload.therapist = payload.therapist._id;
    }
    if (payload.payments) {
      if (typeof payload.payments[0] === 'object') {
        payload.payments = payload.payments.map(val => val._id);
      }
    }
    if (payload.patient && typeof payload.patient === 'object') {
      payload.patient = payload.patient._id;
    }
    if (payload.clinic && typeof payload.clinic === 'object') {
      payload.clinic = payload.clinic._id;
    }

    // TODO: chan - there should be mapperfromInvoice to JSON and vice versa
    payload.promotions = payload.promotions.map((promotion) => {
      if (typeof promotion === 'object' && (promotion.id || promotion._id)) {
        return promotion.id || promotion._id;
      }
      return promotion;
    });
    delete payload.queue;
    delete payload.patient;
    if (voidInvoice) {
      const { items } = payload;
      payload = {
        invoiceNo: id,
        voided: true,
        items,
      };
    }

    if (closeInvoice) {
      payload.finalized = true;
    }
    const resp = await consultationService.updateInvoice(id, payload, closeInvoice);
    if (resp.success) {
      const updatedInvoice = resp.data;
      const { activePrescription, selectedConsultation } = _state;
      if (clonedMainFormId) {
        updatedInvoice.mainFormId = clonedMainFormId;
      }
      updatedInvoice.clinic = activePrescription.clinic;

      const { consultationList } = _state;
      let updateConsultation = null;
      const newList = cloneDeep(consultationList || []).map((c) => {
        if (c._id !== selectedConsultation._id) return c;

        c.order = selectedConsultation.order.map(o => {
          if (o._id !== updatedInvoice._id) return o;
          o = updatedInvoice;
          return o;
        })
        updateConsultation = cloneDeep(c);
        return c;
      });
      commit(SET_TCM_CONSULTATION_LIST, newList);
      if (updateConsultation) {
        commit(SET_TCM_ACTIVE_CONSULTATION, updateConsultation);
      }

      commit(UPDATE_INVOICE, updatedInvoice);
    }
    return resp;
  },
  async saveAllPrescriptions({ commit, state: _state }, { invoiceConfig, voidInvoice, closeInvoice }) {
    if (isEmpty(_state.selectedConsultation)) return;
    const { order } = _state.selectedConsultation;
    if (!order) return;
    const invoiceIdList = order.map((o) => {
      if (typeof o === 'string') { return o; }
      return o._id;
    });

    invoiceIdList.forEach((idx) => {
      const invoice = _state.invoicesMappedById[idx];

      if (!invoice) return;
      if (invoice.invoiceNo === 'draft') {
        const params = {
          invoice,
          includeGST: invoiceConfig.includeGST,
        };
        if (invoiceConfig.exchangeRate) {
          params.exchangeRate = invoiceConfig.exchangeRate;
          commit(UPDATE_INVOICE_GST_X_RATE, params);
        } else {
          commit(UPDATE_INVOICE_GST, params);
        }
      }
    });

    let invoices = invoiceIdList.map(idx => _state.invoicesMappedById[idx]).filter(i => !!i._id);

    invoices = invoices.map((ite) => {
      const adminAddedItems = ite.items.map(item => ({
        ...item,
        isAddedByAdmin: true,
      }));
      return {
        ...ite,
        items: adminAddedItems,
      };
    });

    // return;

    const promises = invoices.map((invoice) => {
      let payload = Object.assign({}, invoice);
      const clonedMainFormId = { ...payload.mainFormId };
      // misc
      payload.amountApply = 0;
      payload.corpAmountApply = 0;

      if (payload.queue) {
        if (payload.queue.provider && !payload.provider) payload.provider = payload.queue.provider;
        if (payload.queue.therapist && !payload.therapist) {
          payload.therapist = payload.queue.therapist;
        }
      }

      if (payload.provider && typeof payload.provider === 'object') {
        payload.provider = payload.provider._id;
      }
      if (payload.clinic && typeof payload.clinic === 'object') {
        payload.clinic = payload.clinic._id;
      }
      if (payload.therapist && typeof payload.therapist === 'object') {
        payload.therapist = payload.therapist._id;
      }
      if (payload.payments) {
        if (typeof payload.payments[0] === 'object') {
          payload.payments = payload.payments.map(val => val._id);
        }
      }

      // TODO: chan - there should be mapperfromInvoice to JSON and vice versa
      payload.promotions = payload.promotions.map((promotion) => {
        if (typeof promotion === 'object' && (promotion.id || promotion._id)) {
          return promotion.id || promotion._id;
        }
        return promotion;
      });
      delete payload.queue;
      delete payload.patient;
      if (voidInvoice) {
        const { items } = payload;
        payload = {
          invoiceNo: invoice._id,
          voided: true,
          items,
        };
      }

      if (closeInvoice) {
        payload.finalized = true;
      }
      return new Promise((resolve, reject) => {
        consultationService.updateInvoice(invoice._id, payload, closeInvoice).then((resp) => {
          if (resp.success) {
            const updatedInvoice = resp.data;
            if (clonedMainFormId) {
              updatedInvoice.mainFormId = clonedMainFormId;
            }
            commit(UPDATE_INVOICE, updatedInvoice);
          }
          resolve(resp);
        }).catch((e) => {
          reject(e);
        });
      });
    });
    await Promise.all(promises).then(() => {
      console.log('done');
    });
  },
  loadInvoiceItems({ commit }, invoiceId) {
    return consultationService.getInvoiceByShowId(invoiceId)
      .then((response) => {
        let payload = {};
        if (response.success) {
          payload = response.data;
        }
        // commit(UPDATE_INVOICE, payload);
        return payload;
      });
  },
  setPrintTreatmentRecordConsultation({ commit }, consultation) {
    commit(SET_PRINT_TREATMENT_RECORD_CONSULTATION, consultation);
  },
  setPrintPrescriptionConsultation({ commit }, { consultation, printWithDailyDosage }) {
    commit(SET_PRINT_PRESCRIPTION_CONSULTATION, { consultation, printWithDailyDosage });
  },
  resetUpdatedDataset({ commit }) {
    commit(RESET_UPDATED_DATASET)
  }
};

const getters = {
  printPayload: _state => _state.tcmPrescriptionPrintPayload,
  selectedConsultationIdByQueue: _state => _state.selectedConsultationIdByQueue,
  consultationList: _state => _state.consultationList,
  consultationHistoryList: (_state, _getters) =>
    _getters.consultationList.filter(
      c => c.status === constant.TCM_CONSULTATION_STATUS.FINALIZED
    ),
  selectedConsultationHistory: _state => _state.selectedConsultationHistory,
  activeHistoryPrescription: _state => _state.activeHistoryPrescription,
  selectedConsultation: _state => _state.selectedConsultation,
  activePrescription: _state => _state.activePrescription,
  openConsultationList: (_state, _getters, _rootState, rootGetters) => {
    const patientConfig = rootGetters['config/patient'];
    let openConsList = _getters.consultationList;
    if (!patientConfig || !patientConfig.enableDiagnosisTreatmentAddendum) {
      openConsList = openConsList.filter(
        c => c.status !== constant.TCM_CONSULTATION_STATUS.FINALIZED
      );
    }

    return openConsList;
  },
  visitRecordForm: _state => _state.visitRecordForm,
  activeTab: _state => _state.activeTab,
  referRootShortcuts: _state => _state.referRootShortcuts,
  invoicesMappedById: _state => _state.invoicesMappedById,
  invoicesToPrint: _state => _state.invoicesToPrint,
  dataToPrint: _state => _state.dataToPrint,
  treatmentRecordConsultationToPrint: _state => _state.treatmentRecordConsultationToPrint,
  prescriptionInConsultationToPrint: _state => _state.prescriptionInConsultationToPrint,
  activePrescriptions: (_state) => {
    const { order } = _state.selectedConsultation;
    if (!order) {
      return [];
    }
    const ids = order.map((i) => {
      if (typeof i === 'string') return i;
      return i._id;
    });
    const prescriptions = ids.map(id => _state.invoicesMappedById[id]);
    return prescriptions;
  },
  consultationLastUpdated: _state => _state.consultationLastUpdated,
  orderLastUpdated: _state => _state.orderLastUpdated,
  visitRecordLastUpdated: _state => _state.visitRecordLastUpdated,
  invoiceLastUpdated: _state => _state.invoiceLastUpdated,
  copyConsultationLastUpdated: _state => _state.copyConsultationLastUpdated,
  isFetchLastData: _state => _state.isFetchLastData
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
